import * as Plot from "@observablehq/plot";
import { useCallback, useEffect, useMemo, useState } from "react";

import Graphique from "./Graphique";
import Maths from "./Maths";
import { api, type Document, type ResultatsQcm as Resultats } from "./api";
import { couleursNiveaux, neutres, niveauPourTaux, sequentielle } from "./palette";

const NOMBRE = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 });

function Indicateur({ libelle, valeur }: { libelle: string; valeur: string }) {
  return (
    <div className="indicateur">
      <span className="libelle">{libelle}</span>
      <span className="valeur">{valeur}</span>
    </div>
  );
}

export default function ResultatsQcm({ qcmId }: { qcmId: number }) {
  const [resultats, setResultats] = useState<Resultats | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [encours, setEncours] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const recharger = useCallback(() => {
    api.lireResultatsQcm(qcmId).then(setResultats);
    api.lireDocumentsQcm(qcmId).then(setDocuments);
  }, [qcmId]);

  useEffect(recharger, [recharger]);

  const stats = resultats?.classe_stats;

  // Classes d'un point, comme l'histogramme du PDF : les deux représentations
  // doivent montrer la même chose.
  const distribution = useMemo(() => {
    if (!stats) return [];
    const classes = new Map<number, number>();
    for (const note of stats.notes) {
      const classe = Math.round(note);
      classes.set(classe, (classes.get(classe) ?? 0) + 1);
    }
    return [...classes.entries()]
      .map(([note, effectif]) => ({ note, effectif }))
      .sort((a, b) => a.note - b.note);
  }, [stats]);

  // Les questions portent l'identifiant de la banque — « frac-simplifier-21-63 »
  // décrit son contenu, pas sa place ici. Sur les axes, c'est le numéro de la
  // copie qui parle au professeur ; l'énoncé suit dans le tableau.
  const donneesQuestions = useMemo(() => {
    if (!resultats || !stats) return [];
    return resultats.questions.map((question, index) => ({
      numero: `Q${index + 1}`,
      ref: question.ref,
      enonce: question.label,
      taux: (stats.reussite_par_question[question.ref] ?? 0) * 100,
    }));
  }, [resultats, stats]);

  const donneesCompetences = useMemo(() => {
    if (!resultats || !stats) return [];
    return resultats.competences.map((competence) => ({
      code: competence.code,
      libelle: competence.libelle,
      taux: (stats.reussite_par_competence[competence.code] ?? 0) * 100,
    }));
  }, [resultats, stats]);

  const produireCorrige = async () => {
    setEncours(true);
    setMessage(null);
    try {
      const document = await api.produireCorrigeQcm(qcmId);
      setMessage(`${Math.round(document.octets / 1024)} ko produits.`);
      setDocuments(await api.lireDocumentsQcm(qcmId));
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setEncours(false);
    }
  };

  if (!resultats || !stats) return <p>Chargement…</p>;

  if (stats.effectif === 0 || stats.notes.length === 0) {
    return (
      <p className="aide">
        Aucune réponse saisie : les résultats apparaîtront dès les premières
        copies relevées.
      </p>
    );
  }

  const corrige = documents.find((d) => d.type === "corrige_classe");
  const bareme = resultats.total_points;

  return (
    <section className="statistiques">
      <header className="saisie-entete">
        <div>
          <h2>Résultats — {resultats.titre}</h2>
          <p className="sous-titre">
            {stats.effectif} copies
            {stats.absents.length > 0 && ` · ${stats.absents.length} absents`}
            {` · score sur ${NOMBRE.format(bareme)}`}
          </p>
        </div>
        <div className="actions">
          <button type="button" onClick={produireCorrige} disabled={encours}>
            {encours ? "Composition…" : "Produire le corrigé de classe"}
          </button>
          {corrige && (
            <a className="telechargement" href={api.lienDocumentQcm(corrige.id)}>
              Télécharger le corrigé
            </a>
          )}
        </div>
      </header>

      {message && <p className="message">{message}</p>}

      <p className="aide">
        Le barème d'un QCM est son nombre de questions : le score reste brut,
        jamais ramené à 20. Le corrigé de classe se projette puis se dépose sur
        l'espace numérique — il porte les énoncés, donc se lit sans le sujet.
      </p>

      <div className="indicateurs">
        <Indicateur libelle="Moyenne" valeur={NOMBRE.format(stats.moyenne)} />
        <Indicateur libelle="Médiane" valeur={NOMBRE.format(stats.mediane)} />
        <Indicateur libelle="Écart-type" valeur={NOMBRE.format(stats.ecart_type)} />
        <Indicateur libelle="Minimum" valeur={NOMBRE.format(stats.minimum)} />
        <Indicateur libelle="Maximum" valeur={NOMBRE.format(stats.maximum)} />
      </div>
      <p className="aide">
        Un quart des élèves sous {NOMBRE.format(stats.quartiles[0])}, la moitié
        sous {NOMBRE.format(stats.quartiles[1])}, trois quarts sous{" "}
        {NOMBRE.format(stats.quartiles[2])}.
      </p>

      <h3>Distribution des scores</h3>
      <Graphique
        options={{
          height: 200,
          marginLeft: 42,
          x: { label: `score sur ${bareme}`, domain: [-0.5, bareme + 0.5] },
          y: { label: "élèves", grid: true },
          style: { fontSize: "11px" },
          marks: [
            Plot.rectY(distribution, {
              x1: (d: { note: number }) => d.note - 0.5,
              x2: (d: { note: number }) => d.note + 0.5,
              y: "effectif",
              fill: sequentielle.hue,
              insetLeft: 0.5,
              insetRight: 0.5,
            }),
            Plot.ruleX([stats.moyenne], {
              stroke: neutres.texte_secondaire, strokeDasharray: "4 2",
            }),
            Plot.ruleY([0], { stroke: neutres.axe }),
          ],
        }}
      />

      <h3>Réussite par question</h3>
      <Graphique
        options={{
          height: 24 * donneesQuestions.length + 40,
          marginLeft: 42,
          x: {
            label: "réussite", domain: [0, 100], grid: true,
            tickFormat: (t: number) => `${t} %`,
          },
          y: { label: null, domain: donneesQuestions.map((q) => q.numero) },
          style: { fontSize: "11px" },
          marks: [
            Plot.barX(donneesQuestions, {
              x: "taux", y: "numero", fill: sequentielle.hue, sort: null,
            }),
            Plot.text(donneesQuestions, {
              x: "taux", y: "numero",
              text: (d: { taux: number }) => `${Math.round(d.taux)} %`,
              textAnchor: "start", dx: 4, fill: neutres.texte_secondaire,
            }),
            Plot.ruleX([0], { stroke: neutres.axe }),
          ],
        }}
      />

      <table className="questions-qcm">
        <tbody>
          {donneesQuestions.map((question) => (
            <tr key={question.ref}>
              <th>{question.numero}</th>
              <td>
                {/* L'énoncé est ici le contenu, pas l'aperçu d'une zone de
                    saisie : une question sans formule doit s'afficher quand
                    même — `ApercuTypst` se tairait. */}
                <div className="enonce-qcm">
                  <Maths texte={question.enonce} syntaxe="typst" />
                </div>
                <div className="fichier">{question.ref}</div>
              </td>
              <td
                className="nombre"
                style={{
                  color: couleursNiveaux[niveauPourTaux(question.taux / 100)],
                }}
              >
                {Math.round(question.taux)} %
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {donneesCompetences.length > 0 && (
        <>
          <h3>Réussite par compétence</h3>
          <Graphique
            options={{
              height: 26 * donneesCompetences.length + 40,
              marginLeft: 300,
              x: {
                label: "réussite", domain: [0, 100], grid: true,
                tickFormat: (t: number) => `${t} %`,
              },
              y: { label: null, domain: donneesCompetences.map((c) => c.libelle) },
              style: { fontSize: "11px" },
              marks: [
                Plot.barX(donneesCompetences, {
                  x: "taux", y: "libelle", fill: sequentielle.hue, sort: null,
                }),
                Plot.text(donneesCompetences, {
                  x: "taux", y: "libelle",
                  text: (d: { taux: number }) => `${Math.round(d.taux)} %`,
                  textAnchor: "start", dx: 4, fill: neutres.texte_secondaire,
                }),
                Plot.ruleX([0], { stroke: neutres.axe }),
              ],
            }}
          />
        </>
      )}

      {donneesCompetences.length === 0 && (
        <p className="aide">
          Aucune compétence n'est encore rattachée aux questions : la réussite
          par compétence et le tableau des bilans resteront vides jusque-là.
        </p>
      )}
    </section>
  );
}
